"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import ChatSheet from "@/components/ChatSheet";
import SettingsSheet from "@/components/SettingsSheet";
import NewDecisionModal from "@/components/review/NewDecisionModal";
import HelpVideosOverlay from "@/components/help/HelpVideosOverlay";

type DecisionCreatedHandler = (notice: string) => void;

interface OverlayManagerContextValue {
  isChatOpen: boolean;
  isSettingsOpen: boolean;
  isNewDecisionOpen: boolean;
  isHelpOpen: boolean;
  openChat: (initialMessage?: string | null) => void;
  closeChat: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  openNewDecision: (options?: { onCreated?: DecisionCreatedHandler }) => void;
  closeNewDecision: () => void;
  openHelp: () => void;
  closeHelp: () => void;
  closeAll: () => void;
  /** WIP-1 § 8-7 U5: surface-scoped goal context for chat grounding.
   *  Callers (e.g. GoalsScreen) publish the currently-expanded goal id here;
   *  ChatScreen reads it and only forwards on the Goals surface. */
  focusedGoalId: string | null;
  setFocusedGoal: (goalId: string | null) => void;
}

const OverlayManagerContext = createContext<OverlayManagerContextValue | null>(null);

interface OverlayManagerProviderProps {
  children: ReactNode;
  routeKey: string;
  screenContext: string;
}

export function OverlayManagerProvider({
  children,
  routeKey,
  screenContext,
}: OverlayManagerProviderProps) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInitialMessage, setChatInitialMessage] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newDecisionOpen, setNewDecisionOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // WIP-1 § 8-7 U5: currently-focused goal id, published by the Goals
  // surface so chat can ground against it when the user opens chat.
  const [focusedGoalId, setFocusedGoalIdState] = useState<string | null>(null);
  const createdHandlerRef = useRef<DecisionCreatedHandler | null>(null);

  const setFocusedGoal = useCallback((goalId: string | null) => {
    setFocusedGoalIdState(goalId && goalId.length > 0 ? goalId : null);
  }, []);

  const closeChat = useCallback(() => {
    setChatOpen(false);
    // Clear focused goal when chat closes — next open should re-publish
    // from whatever surface the user is on (GoalsScreen re-fires its
    // effect on mount/activeGoalId change).
    setFocusedGoalIdState(null);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const closeNewDecision = useCallback(() => {
    createdHandlerRef.current = null;
    setNewDecisionOpen(false);
  }, []);

  const closeHelp = useCallback(() => {
    setHelpOpen(false);
  }, []);

  const closeAll = useCallback(() => {
    setChatOpen(false);
    setSettingsOpen(false);
    createdHandlerRef.current = null;
    setNewDecisionOpen(false);
    setHelpOpen(false);
    setFocusedGoalIdState(null);
  }, []);

  const openChat = useCallback((initialMessage?: string | null) => {
    setSettingsOpen(false);
    createdHandlerRef.current = null;
    setNewDecisionOpen(false);
    setHelpOpen(false);
    setChatInitialMessage(initialMessage ?? null);
    setChatOpen(true);
  }, []);

  const openSettings = useCallback(() => {
    setChatOpen(false);
    createdHandlerRef.current = null;
    setNewDecisionOpen(false);
    setHelpOpen(false);
    setSettingsOpen(true);
  }, []);

  const openNewDecision = useCallback((options?: { onCreated?: DecisionCreatedHandler }) => {
    setChatOpen(false);
    setSettingsOpen(false);
    createdHandlerRef.current = options?.onCreated ?? null;
    setHelpOpen(false);
    setNewDecisionOpen(true);
  }, []);

  const openHelp = useCallback(() => {
    setChatOpen(false);
    setSettingsOpen(false);
    createdHandlerRef.current = null;
    setNewDecisionOpen(false);
    setHelpOpen(true);
  }, []);

  const handleDecisionCreated = useCallback((notice: string) => {
    const handler = createdHandlerRef.current;
    createdHandlerRef.current = null;
    setNewDecisionOpen(false);
    handler?.(notice);
  }, []);

  // Revised 2026-05-24 after UAT: ONLY close modal overlays (settings,
  // new decision) on route change. Chat must remain open across
  // route changes so action-chip clicks that trigger a `reveal` to a
  // tab DON'T silently destroy the user's chat history mid-flow.
  // Earlier `closeAll` here meant clicking a `schedule_task` chip
  // navigated to Plan AND wiped the follow-up message that contained
  // the next action — breaking the multi-turn schedule loop.
  // User-driven close is still explicit via the chat X button.
  useEffect(() => {
    setSettingsOpen(false);
    createdHandlerRef.current = null;
    setNewDecisionOpen(false);
    setHelpOpen(false);
  }, [routeKey]);

  const value = useMemo<OverlayManagerContextValue>(
    () => ({
      isChatOpen: chatOpen,
      isSettingsOpen: settingsOpen,
      isNewDecisionOpen: newDecisionOpen,
      isHelpOpen: helpOpen,
      openChat,
      closeChat,
      openSettings,
      closeSettings,
      openNewDecision,
      closeNewDecision,
      openHelp,
      closeHelp,
      closeAll,
      focusedGoalId,
      setFocusedGoal,
    }),
    [
      chatOpen,
      settingsOpen,
      newDecisionOpen,
      helpOpen,
      openChat,
      closeChat,
      openSettings,
      closeSettings,
      openNewDecision,
      closeNewDecision,
      openHelp,
      closeHelp,
      closeAll,
      focusedGoalId,
      setFocusedGoal,
    ]
  );

  return (
    <OverlayManagerContext.Provider value={value}>
      {children}

      <ChatSheet
        open={chatOpen}
        onOpen={() => openChat()}
        onClose={closeChat}
        screenContext={screenContext}
        initialMessage={chatInitialMessage}
        onInitialMessageConsumed={() => setChatInitialMessage(null)}
        focusedGoalId={focusedGoalId}
        // Hide the mobile floating Chat trigger whenever another
        // overlay is on top so the two stop visually layering.
        triggerHidden={settingsOpen || newDecisionOpen || helpOpen}
      />

      <SettingsSheet open={settingsOpen} onClose={closeSettings} />

      {newDecisionOpen && (
        <NewDecisionModal
          onClose={closeNewDecision}
          onCreated={handleDecisionCreated}
        />
      )}

      <HelpVideosOverlay open={helpOpen} onClose={closeHelp} />
    </OverlayManagerContext.Provider>
  );
}

export function useOverlayManager() {
  const value = useContext(OverlayManagerContext);
  if (!value) {
    throw new Error("useOverlayManager must be used within an OverlayManagerProvider");
  }
  return value;
}
